import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTORS = [
  { id: "ristorante", label: "Ristorante", bio: "Cucina d'autore, atmosfera indimenticabile. Prenota il tuo tavolo." },
  { id: "artista", label: "Artista", bio: "Nuovo singolo fuori ora. Tour 2026 — biglietti in bio." },
  { id: "bar", label: "Bar", bio: "Cocktail signature & mixology. Ogni sera, un rituale." },
  { id: "parrucchiere", label: "Parrucchiere", bio: "Hair styling su misura. Prenota la tua trasformazione." },
  { id: "hotel", label: "Hotel", bio: "Boutique resort. Il lusso di sentirsi altrove." },
  { id: "beauty", label: "Beauty", bio: "Beauty lab & aesthetics. La tua pelle, la nostra arte." },
];

const STYLES = [
  { id: "minimal", label: "Minimal Luxury", desc: "Linee pulite, spazi ampi, tipografia essenziale", covers: [4], coverTone: "light" },
  { id: "editorial", label: "Editorial Premium", desc: "Vibe da magazine, layout audace, copertine tipografiche", covers: [1, 7], coverTone: "contrast" },
  { id: "cinematic", label: "Cinematic Luxury", desc: "Toni cupi, luci soffuse, profondità cinematografica", covers: [2, 6], coverTone: "dark" },
];

const PALETTES = [
  { id: "velvet", name: "Velvet Purple", colors: ["#05050A", "#9333EA", "#C084FC"], filter: "saturate(1.05) hue-rotate(-8deg)", deep: "saturate(1.15) hue-rotate(-12deg) contrast(1.05)" },
  { id: "champagne", name: "Champagne Gold", colors: ["#1A1714", "#D4AF37", "#F3E5AB"], filter: "sepia(.16) saturate(1.1)", deep: "sepia(.28) saturate(1.2) contrast(1.04)" },
  { id: "emerald", name: "Emerald Noir", colors: ["#06140E", "#10B981", "#E2E8F0"], filter: "saturate(.95) hue-rotate(8deg)", deep: "saturate(1.02) hue-rotate(14deg) contrast(1.06)" },
  { id: "cyber", name: "Cyber Neon", colors: ["#0D021A", "#EC4899", "#8B5CF6"], filter: "saturate(1.2) contrast(1.06)", deep: "saturate(1.35) contrast(1.12) brightness(.96)" },
];

const SECTOR_IMAGES = {
  ristorante: [
    "https://images.unsplash.com/photo-1694021408920-922ff450c525?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1616671285410-2a676a9a433d?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1676471926534-d5c9771909fa?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1570560258879-af7f8e1447ac?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1583354608715-177553a4035e?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1556855810-ac404aa91e85?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  ],
  artista: [
    "https://images.unsplash.com/photo-1658091747909-91188be4df87?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1760050516412-c8b92f3e38b2?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1791337163175-ba2b09e20ea5?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1786319222476-2d62842ad778?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  ],
  bar: [
    "https://images.unsplash.com/photo-1597075687490-8f673c6c17f6?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1607622750671-6cd9a99eabd1?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1556855810-ac404aa91e85?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1514933651103-005eec06c04b?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1551024709-8f23befc6f87?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1536935338788-846bb9981813?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  ],
  parrucchiere: [
    "https://images.unsplash.com/photo-1554519934-e32b1629d9ee?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1562322140-8baeececf3df?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1560869713-7d0a29430803?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1595476108010-b4d1f102b1b1?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1605497788044-5a32c7078486?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1560066984-138dadb4c035?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  ],
  hotel: [
    "https://images.unsplash.com/photo-1731336479432-3eb5fdb3ab1c?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1680609989998-6183fcea718b?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1586611292717-f828b167408c?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1582719508461-905c673771fd?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1540555700478-4be289fbecef?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1618773928121-c32242e63f39?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
  ],
  beauty: [
    "/assets/beautin-glamour-1.webp", "/assets/beautin-glamour-2.webp", "/assets/beautin-glamour-3.webp",
    "/assets/beautin-glamour-4.webp", "/assets/beautin-aesthetic-1.webp", "/assets/beautin-aesthetic-2.webp",
    "/assets/beautin-bottles.jpg", "/assets/beautin-highlighter.webp", "/assets/beautin-woman.jpg",
  ],
};

const COVER_TEXTS = {
  ristorante: ["MENU", "LA CUCINA", "DEGUSTAZIONE"],
  artista: ["LIVE", "NUOVO SINGOLO", "BACKSTAGE"],
  bar: ["SIGNATURE", "MIXOLOGY", "APERTIVO"],
  parrucchiere: ["IL TAGLIO", "STILE", "COLOR LAB"],
  hotel: ["LA SUITE", "ESPERIENZE", "WELLNESS"],
  beauty: ["GLOW", "SKIN RITUAL", "BEAUTY LAB"],
};

const INTENSITY_LABELS = ["Moderata", "Profonda", "Rivoluzione totale"];

const cls = (...a) => a.filter(Boolean).join(" ");

function Monogram({ text, colors, size = 56 }) {
  const initials = (text || "SW").replace(/^@/, "").split(/[\s._-]+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase() || "SW";
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white shrink-0"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${colors[1]}, ${colors[2]})`, fontFamily: "Syne, sans-serif", fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
}

function Highlights({ colors, dark }) {
  return (
    <div className="flex gap-3 px-3 py-2">
      {["Novità", "Best", "Press"].map((h) => (
        <div key={h} className="flex flex-col items-center gap-1">
          <div className="w-11 h-11 rounded-full p-[2px]" style={{ background: `conic-gradient(${colors[1]}, ${colors[2]}, ${colors[1]})` }}>
            <div className={cls("w-full h-full rounded-full", dark ? "bg-[#111]" : "bg-white")} />
          </div>
          <span className={cls("text-[9px]", dark ? "text-slate-400" : "text-slate-500")}>{h}</span>
        </div>
      ))}
    </div>
  );
}

function AfterGrid({ sector, style, palette, intensity }) {
  const images = SECTOR_IMAGES[sector.id];
  const texts = COVER_TEXTS[sector.id];
  const covers = intensity >= 3 ? [...style.covers, 8] : intensity >= 2 ? [...style.covers, style.covers[0] === 4 ? 0 : 4] : style.covers.slice(0, 1);
  const filter = intensity >= 2 ? palette.deep : palette.filter;
  const tiles = [];
  let coverIdx = 0;
  for (let i = 0; i < 9; i++) {
    if (covers.includes(i)) {
      const label = texts[coverIdx++ % texts.length];
      tiles.push(
        <div key={i} className="aspect-square flex items-center justify-center p-2 text-center"
          style={{ background: style.coverTone === "light" ? palette.colors[2] : style.coverTone === "dark" ? palette.colors[0] : palette.colors[1] }}>
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.05, fontSize: "clamp(9px, 1.6vw, 15px)", color: style.coverTone === "light" ? palette.colors[0] : "#fff" }}>
            {label}
          </span>
        </div>
      );
    } else {
      tiles.push(
        <div key={i} className="aspect-square overflow-hidden">
          <img src={images[i % images.length]} alt="" loading="lazy" className="w-full h-full object-cover" style={{ filter }} />
        </div>
      );
    }
  }
  return <div className="grid grid-cols-3 gap-[2px]" data-testid="transformation-after-grid">{tiles}</div>;
}

function AfterProfile({ nickname, sector, style, palette, intensity }) {
  const handle = (nickname || "iltuobrand").replace(/^@/, "");
  return (
    <div className="bg-[#0B0B14] text-white">
      <div className="flex items-center gap-4 p-3">
        <Monogram text={handle} colors={palette.colors} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate" style={{ fontFamily: "Syne, sans-serif" }}>{handle.toLowerCase()}</p>
          <p className="text-[11px] text-slate-400 leading-snug line-clamp-2">{sector.bio}</p>
        </div>
        <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: palette.colors[1], fontFamily: "Syne, sans-serif" }}>Segui</span>
      </div>
      <Highlights colors={palette.colors} dark />
      <AfterGrid sector={sector} style={style} palette={palette} intensity={intensity} />
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.2em] font-mono" style={{ color: palette.colors[1] }}>by Social Whale</span>
        <span className="text-[9px] text-slate-500">{style.label}</span>
      </div>
    </div>
  );
}

function BeforeProfile({ nickname, screenshot }) {
  const handle = nickname ? nickname.replace(/^@/, "") : null;
  return (
    <div className="bg-white text-slate-900">
      <div className="flex items-center gap-4 p-3">
        {screenshot ? (
          <div className="w-14 h-14 rounded-full bg-slate-200 shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 font-bold shrink-0" style={{ fontFamily: "Syne, sans-serif" }}>
            {handle ? handle.slice(0, 2).toUpperCase() : "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{handle || "il-tuo-profilo"}</p>
          <p className="text-[11px] text-slate-500 leading-snug">{handle ? `@${handle} — profilo attuale` : "Il tuo profilo attuale"}</p>
        </div>
      </div>
      {screenshot ? (
        <img src={screenshot} alt="Screenshot del profilo caricato dall'utente" className="w-full object-cover" />
      ) : (
        <div className="grid grid-cols-3 gap-[2px] px-[2px] pb-2">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="aspect-square bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center">
              <span className="text-[9px] text-slate-400 font-mono">post</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Transformation() {
  const [nickname, setNickname] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [sector, setSector] = useState(SECTORS[0]);
  const [style, setStyle] = useState(STYLES[0]);
  const [palette, setPalette] = useState(PALETTES[0]);
  const [intensity, setIntensity] = useState(2);
  const [pos, setPos] = useState(50);
  const [autoDemo, setAutoDemo] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", attivita: "", email: "", telefono: "", consenso: false });
  const [sending, setSending] = useState(false);
  const rafRef = useRef(null);
  const userTouched = useRef(false);

  // Cropper state
  const [cropSrc, setCropSrc] = useState(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOff, setCropOff] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const imgRef = useRef(null);

  // Auto-demo oscillation
  useEffect(() => {
    if (!autoDemo) { if (rafRef.current) cancelAnimationFrame(rafRef.current); return; }
    let start = null;
    const tick = (t) => {
      if (!start) start = t;
      const p = 50 + 32 * Math.sin((t - start) / 1400);
      setPos(Math.round(p));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [autoDemo]);

  const onSlider = (v) => {
    if (!userTouched.current) { userTouched.current = true; setAutoDemo(false); }
    setPos(Math.max(0, Math.min(100, Number(v))));
  };

  // Drag diretto sul visualizzatore: funziona con dito/mouse da QUALUNQUE punto
  // (il range nativo invisibile su iOS si aggancia solo toccando il thumb).
  const sliderBoxRef = useRef(null);
  const sliderDragging = useRef(false);
  const posFromPointer = (e) => {
    const box = sliderBoxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    onSlider(((e.clientX - r.left) / r.width) * 100);
  };
  const onSliderPointerDown = (e) => {
    sliderDragging.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    posFromPointer(e);
  };
  const onSliderPointerMove = (e) => { if (sliderDragging.current) posFromPointer(e); };
  const onSliderPointerUp = () => { sliderDragging.current = false; };

  // ---------- cropper ----------
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result); setCropZoom(1); setCropOff({ x: 0, y: 0 }); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  const onCropPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, ox: cropOff.x, oy: cropOff.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onCropPointerMove = (e) => {
    if (!dragRef.current) return;
    setCropOff({ x: dragRef.current.ox + (e.clientX - dragRef.current.x), y: dragRef.current.oy + (e.clientY - dragRef.current.y) });
  };
  const onCropPointerUp = () => { dragRef.current = null; };
  const applyCrop = () => {
    const img = imgRef.current;
    if (!img) return;
    const box = 320; // anteprima quadrata
    const canvas = document.createElement("canvas");
    canvas.width = 1080; canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    const scale = (box * cropZoom) / Math.min(img.naturalWidth, img.naturalHeight);
    const dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
    const dx = (1080 / 2) + (cropOff.x / box) * 1080 - dw * (1080 / box) / 2;
    const dy = (1080 / 2) + (cropOff.y / box) * 1080 - dh * (1080 / box) / 2;
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, 1080, 1080);
    ctx.drawImage(img, dx, dy, dw * (1080 / box), dh * (1080 / box));
    setScreenshot(canvas.toDataURL("image/jpeg", 0.85));
    setCropSrc(null);
    toast.success("Screenshot allineato. Resta solo nel tuo browser: non viene caricato da nessuna parte.");
  };

  // ---------- lead ----------
  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consenso) { toast.error("Per ricontattarti serve il consenso privacy."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/transformation/lead`, {
        name: lead.name, attivita: lead.attivita, email: lead.email,
        telefono: lead.telefono, nickname, settore: sector.label, stile: style.label,
        consenso_crm: lead.consenso,
      });
      toast.success("Richiesta inviata! Il team Social Whale ti ricontatterà a breve.");
      setLeadOpen(false);
      setLead({ name: "", attivita: "", email: "", telefono: "", consenso: false });
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici dal modulo contatti.");
    } finally {
      setSending(false);
    }
  };

  const scrollToContact = () => {
    const el = document.getElementById("contattaci");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="transformation" data-testid="section-transformation" className="relative w-full py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-400 mb-3">The Transformation</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: "Syne, sans-serif" }}>
            Il tuo profilo, <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg,#9333EA,#3B82F6)" }}>ripensato da zero.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-sm sm:text-base leading-relaxed">
            Inserisci il tuo nickname o carica uno screenshot del profilo, scegli settore e stile, e trascina lo slider per vedere la trasformazione firmata Social Whale.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ---- Controlli ---- */}
          <div className="lg:col-span-4 space-y-6 bg-[#0B0B14]/80 backdrop-blur-xl p-6 rounded-2xl border border-purple-500/20">
            <div>
              <p className="text-sm font-semibold text-purple-200 mb-2">1. Il tuo profilo Instagram</p>
              <div className="flex items-center gap-2">
                <span className="text-purple-400 font-bold">@</span>
                <input data-testid="transformation-nickname-input" value={nickname} onChange={(e) => setNickname(e.target.value.replace(/[^A-Za-z0-9._]/g, ""))}
                  placeholder="nickname" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500" />
              </div>
              <label className="mt-3 flex items-center justify-center gap-2 border border-dashed border-purple-500/40 rounded-lg px-3 py-2.5 text-xs text-purple-300 cursor-pointer hover:bg-purple-500/10 transition-colors">
                <input data-testid="transformation-upload-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
                {screenshot ? "Screenshot caricato ✓ — sostituisci" : "Carica uno screenshot del profilo"}
              </label>
              <p className="mt-1.5 text-[10px] text-slate-500">Lo screenshot resta sul tuo dispositivo: non viene mai caricato sui nostri server.</p>
            </div>

            <div>
              <p className="text-sm font-semibold text-purple-200 mb-2">2. Settore</p>
              <div className="grid grid-cols-3 gap-2" data-testid="transformation-sector-select">
                {SECTORS.map((s) => (
                  <button key={s.id} type="button" onClick={() => setSector(s)}
                    className={cls("px-2 py-2 rounded-lg text-xs font-semibold border transition-all", sector.id === s.id ? "bg-purple-600 border-purple-500 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:border-purple-500/40")}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-purple-200 mb-2">3. Stile visivo</p>
              <div className="space-y-2" data-testid="transformation-style-select">
                {STYLES.map((s) => (
                  <button key={s.id} type="button" onClick={() => setStyle(s)}
                    className={cls("w-full text-left px-3 py-2.5 rounded-lg border transition-all", style.id === s.id ? "bg-purple-600/20 border-purple-500 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:border-purple-500/40")}>
                    <span className="block text-xs font-bold" style={{ fontFamily: "Syne, sans-serif" }}>{s.label}</span>
                    <span className="block text-[10px] text-slate-400 mt-0.5">{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-purple-200 mb-2">4. Palette e intensità</p>
              <div className="grid grid-cols-2 gap-2" data-testid="transformation-palette-select">
                {PALETTES.map((p) => (
                  <button key={p.id} type="button" onClick={() => setPalette(p)}
                    className={cls("flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all", palette.id === p.id ? "border-purple-500 bg-purple-600/20" : "border-white/10 bg-white/5 hover:border-purple-500/40")}>
                    <span className="flex -space-x-1">
                      {p.colors.map((c) => <span key={c} className="w-3.5 h-3.5 rounded-full border border-white/30" style={{ background: c }} />)}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-200">{p.name}</span>
                  </button>
                ))}
              </div>
              <label className="block mt-4 text-[11px] text-slate-400">
                Intensità: <strong className="text-purple-300">{INTENSITY_LABELS[intensity - 1]}</strong>
                <input data-testid="transformation-intensity-slider" type="range" min="1" max="3" step="1" value={intensity}
                  onChange={(e) => setIntensity(Number(e.target.value))} className="tt-range mt-1" aria-label="Intensità della trasformazione" />
              </label>
            </div>
          </div>

          {/* ---- Visualizzatore before/after ---- */}
          <div className="lg:col-span-8">
            <div className="relative rounded-3xl overflow-hidden border border-purple-500/30 bg-[#070710] shadow-2xl">
              <div className="relative select-none" style={{ touchAction: "none" }} ref={sliderBoxRef}
                onPointerDown={onSliderPointerDown} onPointerMove={onSliderPointerMove}
                onPointerUp={onSliderPointerUp} onPointerCancel={onSliderPointerUp}
                data-testid="transformation-compare-area">
                {/* BEFORE (base) */}
                <div className="max-w-md mx-auto"><BeforeProfile nickname={nickname} screenshot={screenshot} /></div>
                {/* AFTER (sopra, rivelato da clip-path) */}
                <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                  <div className="max-w-md mx-auto h-full"><AfterProfile nickname={nickname} sector={sector} style={style} palette={palette} intensity={intensity} /></div>
                </div>
                {/* Etichette */}
                <span className="absolute top-3 right-3 text-[10px] font-mono font-bold tracking-widest px-2.5 py-1 rounded-full bg-black/60 text-slate-300 border border-white/10">BEFORE</span>
                <span className="absolute top-3 left-3 text-[10px] font-mono font-bold tracking-widest px-2.5 py-1 rounded-full text-white border" style={{ background: "rgba(147,51,234,.75)", borderColor: "rgba(147,51,234,.5)" }}>AFTER</span>
                {/* Linea di separazione */}
                <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `calc(${pos}% - 1px)` }}>
                  <div className="w-[2px] h-full mx-auto" style={{ background: "linear-gradient(180deg,#9333EA,#3B82F6)", boxShadow: "0 0 14px rgba(147,51,234,.8)" }} />
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-purple-600 border-2 border-white/70 flex items-center justify-center shadow-lg">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M8 7l-5 5 5 5M16 7l5 5-5 5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
                {/* Slider accessibile (nativo: touch + tastiera) */}
                <input data-testid="transformation-visualizer-slider" type="range" min="0" max="100" value={pos}
                  onChange={(e) => onSlider(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "ArrowLeft") onSlider(pos - 5); if (e.key === "ArrowRight") onSlider(pos + 5); if (e.key === "Home") onSlider(0); if (e.key === "End") onSlider(100); }}
                  className="tt-compare" aria-label="Confronto prima e dopo: trascina per rivelare la trasformazione" />
              </div>
              <div className="flex items-center justify-center gap-3 py-3 border-t border-white/5">
                <button data-testid="transformation-auto-demo-btn" type="button" onClick={() => { userTouched.current = false; setAutoDemo(!autoDemo); }}
                  className={cls("text-xs font-semibold px-4 py-2 rounded-full border transition-all", autoDemo ? "bg-purple-600 border-purple-500 text-white" : "border-white/15 text-slate-300 hover:border-purple-500/50")}>
                  {autoDemo ? "⏸ Ferma demo" : "▶ Demo automatica"}
                </button>
                <button data-testid="transformation-reset-btn" type="button" onClick={() => { setAutoDemo(false); userTouched.current = false; setPos(50); }}
                  className="text-xs font-semibold px-4 py-2 rounded-full border border-white/15 text-slate-300 hover:border-purple-500/50 transition-all">
                  ↺ Ripristina
                </button>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-500 text-center">
              Demo dimostrativa: la proposta “after” usa immagini d’archivio autorizzate. Nessun dato del tuo profilo reale viene letto o inventato.
            </p>
          </div>
        </div>

        {/* ---- Acquisizione contatti ---- */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "Syne, sans-serif" }}>Immagina cosa potremmo fare per il tuo brand.</h3>
          <p className="mt-3 text-slate-400 text-sm max-w-xl mx-auto">Raccontaci chi sei: prepariamo una proposta su misura, con lo stesso livello di cura che hai appena visto.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button type="button" onClick={() => setLeadOpen(true)}
              className="px-6 py-3 rounded-full font-bold text-sm text-white transition-transform hover:scale-105"
              style={{ background: "linear-gradient(135deg,#9333EA,#3B82F6)", fontFamily: "Syne, sans-serif" }}>
              Richiedi una proposta personalizzata
            </button>
            <button type="button" data-testid="chat-social-whale" onClick={scrollToContact}
              className="px-6 py-3 rounded-full font-bold text-sm border border-purple-500/50 text-purple-200 hover:bg-purple-500/10 transition-all"
              style={{ fontFamily: "Syne, sans-serif" }}>
              Parla con Social Whale
            </button>
          </div>

          {leadOpen && (
            <form onSubmit={submitLead} className="mt-8 max-w-md mx-auto text-left bg-[#0B0B14] border border-purple-500/25 rounded-2xl p-6 space-y-3" data-testid="transformation-lead-form">
              <p className="text-sm font-semibold text-purple-200">La tua richiesta</p>
              <input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Il tuo nome *" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500" />
              <input required value={lead.attivita} onChange={(e) => setLead({ ...lead, attivita: e.target.value })} placeholder="Nome della tua attività *" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500" />
              <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email *" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500" />
              <input value={lead.telefono} onChange={(e) => setLead({ ...lead, telefono: e.target.value })} placeholder="Telefono (facoltativo)" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500" />
              <label className="flex items-start gap-2 text-[11px] text-slate-400 leading-snug">
                <input type="checkbox" checked={lead.consenso} onChange={(e) => setLead({ ...lead, consenso: e.target.checked })} className="mt-0.5 accent-purple-500" />
                <span>Acconsento al trattamento dei dati come da <a href="/privacy" className="underline text-purple-300">privacy policy</a> e all'inserimento della mia attività nel CRM commerciale di Social Whale per essere ricontattato.</span>
              </label>
              <button data-testid="submit-transformation-lead" type="submit" disabled={sending}
                className="w-full py-3 rounded-full font-bold text-sm text-white disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#9333EA,#3B82F6)", fontFamily: "Syne, sans-serif" }}>
                {sending ? "Invio in corso…" : "Invia la richiesta"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ---- Cropper screenshot ---- */}
      {cropSrc && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="transformation-cropper-modal">
          <div className="bg-[#0B0B14] border border-purple-500/30 rounded-2xl p-5 w-full max-w-sm">
            <p className="text-sm font-semibold text-white mb-1">Allinea lo screenshot</p>
            <p className="text-[11px] text-slate-400 mb-3">Trascina l'immagine e regola lo zoom. Tutto avviene sul tuo dispositivo.</p>
            <div className="relative w-full aspect-square overflow-hidden rounded-xl bg-slate-800 cursor-grab active:cursor-grabbing"
              style={{ touchAction: "none" }}
              onPointerDown={onCropPointerDown} onPointerMove={onCropPointerMove} onPointerUp={onCropPointerUp} onPointerCancel={onCropPointerUp}>
              <img ref={imgRef} src={cropSrc} alt="Screenshot da ritagliare" draggable={false}
                className="absolute max-w-none"
                style={{
                  width: `${cropZoom * 100}%`,
                  left: `calc(50% + ${cropOff.x}px)`, top: `calc(50% + ${cropOff.y}px)`,
                  transform: "translate(-50%, -50%)",
                }} />
            </div>
            <label className="block mt-3 text-[11px] text-slate-400">Zoom
              <input type="range" min="0.5" max="3" step="0.01" value={cropZoom} onChange={(e) => setCropZoom(Number(e.target.value))} className="tt-range mt-1" aria-label="Zoom screenshot" />
            </label>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setCropSrc(null)} className="flex-1 py-2.5 rounded-full text-xs font-bold border border-white/15 text-slate-300">Annulla</button>
              <button type="button" onClick={applyCrop} className="flex-1 py-2.5 rounded-full text-xs font-bold text-white" style={{ background: "linear-gradient(135deg,#9333EA,#3B82F6)" }}>Usa questa immagine</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
